import { useState } from "react";
import { Popover, MenuItem, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { toggleCategoryProductFee } from "@/services/pricing-config.service";
import { showToast } from "@/redux/actions/toast-actions";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  fontSize: "0.875rem",
  padding: theme.spacing(1, 2),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PopoverContent = styled("div")(({ theme }) => ({
  minWidth: 180,
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

export function FeeConfigActionsPopover({ categoryFee, onEditClick }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const isOpen = Boolean(anchorEl);
  const isActive = categoryFee?.isActive;

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const toggleMutation = useMutation({
    mutationFn: () =>
      toggleCategoryProductFee({ id: categoryFee.id, isActive: !isActive }),
    onSuccess: (data) => {
      dispatch(showToast(data?.message ?? "Updated successfully", "success"));
      queryClient.invalidateQueries({
        queryKey: ["product-category-fees", categoryFee.category?.id],
      }); 
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Failed to update", "error"));
    },
  });

  const handleDeactivateClick = () => {
    handleClose();
    setDeactivateOpen(true);
  };

  const handleDialogClose = () => {
    setDeactivateOpen(false);
    toggleMutation.reset();
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 2,
            sx: { borderRadius: "8px", mt: 0.5 },
          },
        }}
      >
        <PopoverContent>
          <StyledMenuItem
            onClick={() => {
              handleClose();
              onEditClick(categoryFee);
            }}
          >
            Update
          </StyledMenuItem>

          {isActive ? (
            <StyledMenuItem onClick={handleDeactivateClick} danger>
              Deactivate Product
            </StyledMenuItem>
          ) : (
            <StyledMenuItem onClick={handleDeactivateClick}>
              Activate Product
            </StyledMenuItem>
          )}
        </PopoverContent>
      </Popover>

      <CustomConfirmDialog
        open={deactivateOpen}
        onClose={handleDialogClose}
        title={isActive ? "Deactivate Product" : "Activate Product"}
        description={
          isActive
            ? `Are you sure you want to deactivate ${categoryFee?.name ?? "this product for this category"}?`
            : `Are you sure you want to activate ${categoryFee?.name ?? "this product for this category"}?`
        }
        confirmLabel={isActive ? "Deactivate" : "Activate"}
        variant={isActive ? "danger" : "warning"}
        mutation={toggleMutation}
        onConfirm={() => toggleMutation.mutate()}
      />
    </>
  );
}
